import { usePermissions, useTranslate } from '../../customHooks';
import { useCallback, useEffect, useState } from "react";
import {
  ToggleSwitch, CopyButton, Card, CardHeader, CardBody, Button, Sheet, SheetBody,
  CardFooter, RenderModalProps, Modal, ModalHeader, ModalBody, ModalFooter, ModalCloseButton, useModal,
  Link, Label
} from "@panwds/react-ui";
import { PANTitle, toast, PANWDSBannerContainer } from '../../components';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { useAppSelector } from "../../app/hooks";
import { checkCSPBanner } from "../../utils";
import { ApplicationConfigManager } from '../../types';
import styles from './Tenant.module.scss';
import { CircleCheckIcon, ExclamationTriangleIcon, SettingsIcon } from '@panwds/icons';
import { Checkbox, Form, Input, RadioGroup, SubmitButton } from '@panwds/react-form';
import { isEmpty } from 'lodash';
import Tooltip from '../../components/Tooltip/Tooltip';
import { useGetSupportDetailsQuery } from '../../redux/services/support-service';
import { useGetGlobalSettingsQuery, useUpdateGlobalSettingsMutation } from '../../redux/services/gsettings-service';
import { useGetTokensQuery, useUpdateTokensInfoMutation } from '../../redux/services/tokens-service';
import { SUPPORT_ENHANCED, SUPPORT_PREMIUM, SUPPORT_STANDARD } from '../../utils/constants';
import { FormattedMessage } from 'react-intl';
import { useGetUserFederationQuery, useUpdateUserFederationMutation } from '../../redux/services/user-federation-service';
import { getUserName } from '../../utils/utils';
import { FormProvider, useForm } from 'react-hook-form';

interface ProgrammaticAccessUpdateProps {
  action: boolean;
  update: () => Promise<void>;
}

interface FederatedAccessUpdateProps {
  action: boolean;
  update: () => Promise<void>;
}

interface AuditInfo {
  AuditTrail: string | undefined,
  EnableAuditTrail: boolean | undefined,
}

const ProgrammaticAccessConfirmationDialogModal = ({
  closeModal,
  isOpen,
  action,
  update
}: RenderModalProps<ProgrammaticAccessUpdateProps>) => {
  const translate = useTranslate();
  return <Modal dataMetrics="cloudngfw-tokens-edit-confirm-modal"
    onClose={closeModal} isOpen={isOpen} size='sm'>
    <ModalHeader title={translate(`resources.tokens.confirm.title`)} />
    <ModalBody>
      {translate(`resources.tokens.confirm.description`)}
    </ModalBody>
    <ModalFooter>
      <ModalCloseButton>{translate('generic.cancel')}</ModalCloseButton>
      <Button onClick={update} appearance="primary">
        {translate(action ? 'resources.tokens.values.disable' : 'resources.tokens.values.enable')}
      </Button>
    </ModalFooter>
  </Modal>;
};


const FederatedAccessConfirmationDialogModal = ({
  closeModal,
  isOpen,
  update,
  action
}: RenderModalProps<FederatedAccessUpdateProps>) => {
  const translate = useTranslate();
  return <Form
    dataMetrics="cloudngfw-federated-access-confirm-modal"
    modalProps={{ onClose: closeModal, isOpen }}
    onSubmit={update}
  >
    <ModalHeader
      icon={action && <ExclamationTriangleIcon size="md" className="tw-text-orange-400 dark:tw-text-dark-bg-orange" />}
      title={action ? translate('generic.warning') : translate(`generic.notification`)} />
    <ModalBody>
      {action ? translate(`resources.tokens.federatedAccess.confirmDisableTitle`) :
        translate(`resources.tokens.federatedAccess.confirmEnableTitle`)}
      <div className='formLayout'>
        {action ? (
          <>
            <Checkbox
              label={translate('resources.tokens.federatedAccess.confirmDisableOption')}
              name="EnableInvite"
              showLabel
              register={{
                required: translate('validation.required')
              }}
            />
          </>
        ) : (
          <>
            <Checkbox
              label={translate('resources.tokens.federatedAccess.confirmEnableOption1')}
              name="RemoveRoles"
              showLabel
              register={{
                required: translate('validation.required')
              }}
            />
            <Checkbox
              label={translate('resources.tokens.federatedAccess.confirmEnableOption2')}
              name="DisableInvite"
              showLabel
              register={{
                required: translate('validation.required')
              }}
            />
          </>
        )}
      </div>
    </ModalBody>
    <ModalFooter>
      <ModalCloseButton>{translate('generic.cancel')}</ModalCloseButton>
      <SubmitButton addClassName={action ? styles.dangerButton : ''}>{translate(`resources.tokens.values.${action ? 'disable' : 'enable'}`)}</SubmitButton>
    </ModalFooter>
  </Form>
};

const EditAuditLogSheet = ({ isOpen, setOpen, defaultValues, onSubmit }:
  {
    isOpen: boolean, setOpen: React.Dispatch<React.SetStateAction<boolean>>,
    defaultValues: AuditInfo, onSubmit: (info: object) => void
  }) => {
  const translate = useTranslate();
  const formMethods = useForm({ defaultValues: { ...defaultValues, EnableAuditTrail: String(defaultValues.EnableAuditTrail) ?? 'false' } });
  const { watch, setValue } = formMethods;
  const auditTrailEnabled = watch("EnableAuditTrail");

  useEffect(() => {
    if (auditTrailEnabled === 'false') {
      setValue("AuditTrail", "");
    }
  }, [auditTrailEnabled, setValue]);

  return (
    <Sheet title={translate(`resources.tokens.auditLog.edit`)} onClose={() => setOpen(false)} isOpen={isOpen} >
      <SheetBody>
        <Card>
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit)}>
              <CardBody>
                <div className='formLayout'>
                  <RadioGroup label={translate(`resources.tokens.auditLog.auditLog`)}
                    name="EnableAuditTrail"
                    dataMetrics={"cloudngfw-tenant-edit-audit-log"}
                    requiredAsterisk
                    items={[{
                      value: 'false',
                      children: 'None'
                    }, {
                      value: 'true',
                      children: 'CloudWatch'
                    }]} />
                  {auditTrailEnabled === 'true' &&
                    <Input name="AuditTrail"
                      label={translate(`resources.tokens.auditLog.group`)}
                      dataMetrics={"cloudngfw-tenant-edit-audit-log-arn"}
                      requiredAsterisk
                    />
                  }
                </div>
              </CardBody>
              <CardFooter secondaryContent={<SubmitButton />}>
                <Button appearance="secondary"
                  onClick={() => setOpen(false)}
                  dataMetrics="cloudngfw-tokens-edit-save">{translate('generic.cancel')}</Button>
              </CardFooter>
            </form>
          </FormProvider>
        </Card>
      </SheetBody>
    </Sheet >
  )
}

const Tenant = () => {
  const translate = useTranslate();
  const reduxState = useAppSelector((state) => state);
  const { permissions } = usePermissions();
  const [toggleAuditLog, setToggleAuditLog] = useState<boolean>(false);
  const federatedAccessFlagEnabled = import.meta.env.VITE_ENABLE_FEDERATED_ACCESS === "true";

  const [updateTenantSettings] = useUpdateGlobalSettingsMutation();

  const [updateFederatedAccessApi] = useUpdateUserFederationMutation();

  const [updateProgrammaticAccessApi] = useUpdateTokensInfoMutation()

  const { data: cspSupportInfo, error: supportInfoError, isLoading: supportInfoLoading } =
    useGetSupportDetailsQuery({ payload: {} }, {});

  const { data: { EnableAuditTrail, AuditTrail } = {}, error: tenantSettingsError, isLoading: tenantSettingsLoading } =
    useGetGlobalSettingsQuery({}, {});

  const { data: tokensInfo, error: tokensInfoError, isLoading: tokensInfoLoading } =
    useGetTokensQuery({}, {});

  const {
    openModal: programmaticAccessOpenModal, closeModal: programmaticAccessCloseModal
  } = useModal(ProgrammaticAccessConfirmationDialogModal);

  const {
    openModal: federatedAccessOpenModal, closeModal: federatedAccessCloseModal
  } = useModal(FederatedAccessConfirmationDialogModal);

  const { data: userFederationInfo, error: userFederaionError, isLoading: userFederaionLoading, refetch: refetchUserFederation } =
    useGetUserFederationQuery({ UserName: getUserName() }, { skip: !getUserName() || !federatedAccessFlagEnabled });

  const pendoUpdate = useCallback(({ programmaticAccessStatus }: { programmaticAccessStatus: boolean }) => {
    const pendo = window.pendo;
    const userName = getUserName();
    if (userName) {
      pendo.updateOptions({
        visitor: {
          id: userName,
          tenantprogrammaticaccess: programmaticAccessStatus
        },
      });
    }
  }, []);

  //TODO replace with RTK redux state management
  useEffect(() => {
    checkCSPBanner({ translate, reduxState });
  }, [reduxState.support.cspEnable]);

  useEffect(() => {
    if (tokensInfo) {
      pendoUpdate({ programmaticAccessStatus: !tokensInfo?.Enabled });
    }
  }, [tokensInfo]);

  useEffect(() => {
    const errors = [
      { error: supportInfoError, toastId: "support-describe" },
      { error: tenantSettingsError, toastId: "tenant-settings-describe" },
      { error: tokensInfoError, toastId: "tokens-describe" }
    ];

    errors.forEach(({ error, toastId }) => {
      if (error) {
        toast.error(error?.error, { toastId });
      }
    });
  }, [supportInfoError, tenantSettingsError, tokensInfoError]);

  const updateAuditLog = useCallback(
    async (data) => {
      try {
        const result = await updateTenantSettings({ ...data, EnableAuditTrail: data.EnableAuditTrail === 'true' }).unwrap();
        if (result) {
          setToggleAuditLog(false);
        }
      }
      catch (error: any) {
        toast.error(`${error?.code}: ${error?.error}`, { toastId: "tenant-settings-update" });
      }
    }, [permissions]
  );

  const updateProgrammaticAccess = useCallback(
    async (data) => {
      try {
        const result = await updateProgrammaticAccessApi({ Enabled: !tokensInfo?.Enabled }).unwrap();
        if (result) {
          pendoUpdate({ programmaticAccessStatus: tokensInfo?.Enabled ?? false });
          programmaticAccessCloseModal();
        }
      }
      catch (error: any) {
        toast.error(`${error?.code}: ${error?.error}`, { toastId: "tenant-programmatic-access-update" });
      }
    }, [tokensInfo]
  );

  const updateFederatedAccess = useCallback(
    async () => {
      try {
        const result = await updateFederatedAccessApi({ Username: getUserName(), Enabled: !userFederationInfo?.FederatedAccessEnabled }).unwrap();
        if (result) {
          federatedAccessCloseModal();
        }
      }
      catch (error: any) {
        toast.error(`${error?.code}: ${error?.error}`, { toastId: "federated-access-update" });
      }
    }, [userFederationInfo]
  );

  const getAuditTrailText = useCallback(() => {
    if (EnableAuditTrail === undefined) {
      return <>{translate('generic.notApplicable')}</>
    }
    return (EnableAuditTrail ?
      (<>
        <div className={styles.cardFlexContent}>
          <div><CircleCheckIcon className={styles.checkIcon} size='sm' /> </div>
          <div>{translate(`resources.tokens.auditLog.enabledCloudWatch`)}</div>
        </div></>)
      : translate(`resources.tokens.auditLog.disabled`)
    );
  }, [EnableAuditTrail]);

  return (
    <>
      <CircularLoader loading={false} />
      <div className={false ? 'loadingBlur' : ""}>
        <PANTitle divider title={translate(`resources.tokens.title`)} paddingContainer="16px" />
        <PANWDSBannerContainer />
        <div className={styles.cardContainer}>
          <Card>
            <CardHeader addClassName={styles.carHeaderBorder} title={translate(`resources.tokens.tenantVersion.title`)} />
            <CardBody>
              <div className={styles.cardContent}>
                <div><Label label={translate(`resources.tokens.tenantVersion.title`)} /></div>
                <div>{ApplicationConfigManager.getInstance().getConfig().tenantVersion || "V1"}</div>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader addClassName={styles.carHeaderBorder} title={translate(`resources.tokens.fields.access`)} isBackgroundFetching={tokensInfoLoading} />
            <CardBody>
              <div className={styles.cardContent}>
                <div><Label label={translate(`resources.tokens.fields.access`)} /></div>
                <div>
                  <div className={styles.cardFlexContent}>
                    <Tooltip singleLine={false} showTooltip={!reduxState.support.cspEnable} label={translate("csp.registerCSP")} >
                      <div>
                        <div><ToggleSwitch checked={tokensInfo?.Enabled ?? false}
                          onClick={() => programmaticAccessOpenModal({ action: tokensInfo?.Enabled ?? false, update: updateProgrammaticAccess })}
                          disabled={isEmpty(tokensInfo) || !(permissions.CreateProgrammaticAccess)}
                          dataMetrics={"cloudngfw-tenant-edit-programmatic-access"}
                          labelPlacement="right"
                          label={translate(tokensInfo?.Enabled ? 'resources.tokens.values.enabled' : 'resources.tokens.values.disabled')}
                        />
                        </div>
                        <div className={styles.grayText}>{translate('resources.tokens.values.accessSubtitle')}
                          <a target="_blank" href={"https://docs.paloaltonetworks.com/cloud-ngfw/aws/cloud-ngfw-on-aws/getting-started-with-cloud-ngfw-for-aws/enable-programmatic-access"}>{translate('resources.tokens.values.accessSubtitleDoc')}</a>
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
          {federatedAccessFlagEnabled &&
            <Card>
              <CardHeader addClassName={styles.carHeaderBorder} title={translate(`resources.tokens.federatedAccess.title`)}
                isBackgroundFetching={userFederaionLoading} />
              <CardBody>
                <div className={styles.cardContent}>
                  <div><Label label={translate(`resources.tokens.federatedAccess.title`)}
                    tooltip={translate(`resources.tokens.federatedAccess.tooltip`)} /></div>
                  <div><>{userFederaionLoading ?
                    (<></>) :
                    (userFederationInfo?.FederatedAccessEnablement) ?
                      (<div>
                        <div className={styles.cardFlexContent}>
                          <Tooltip showTooltip={true} singleLine={false} label={translate("resources.tokens.federatedAccess.enableTooltip")} >
                            <div>
                              <div><ToggleSwitch checked={userFederationInfo?.FederatedAccessEnabled ?? false}
                                onClick={() => federatedAccessOpenModal({
                                  action: userFederationInfo?.FederatedAccessEnabled ?? false,
                                  update: updateFederatedAccess
                                })}
                                disabled={isEmpty(userFederationInfo) || !(permissions.UpdateFederatedAccess)}
                                dataMetrics={"cloudngfw-tenant-edit-federated-access"}
                                labelPlacement="right"
                                label={translate(userFederationInfo?.FederatedAccessEnabled ? 'resources.tokens.values.enabled' : 'resources.tokens.values.disabled')}
                              />
                              </div>
                              <div className={styles.grayText}>{translate('resources.tokens.federatedAccess.helpText')}
                                <a target="_blank" href={"#"}>{`${translate('generic.document')}.`}</a>
                              </div>
                            </div>
                          </Tooltip>
                        </div>
                      </div>) :
                      (<FormattedMessage
                        id={"resources.tokens.federatedAccess.usageRequirement"}
                        values={{
                          here: (
                            <Link
                              dataMetrics="cloudngfw-federated-access-help"
                              external
                              href="#"
                            >
                              {translate(`generic.here`)}
                            </Link>
                          )
                        }}
                      />)}</>
                  </div>
                </div>
              </CardBody>
            </Card>
          }
          <Card>
            <CardHeader addClassName={styles.carHeaderBorder} title={translate(`resources.tokens.auditLog.title`)} actions={
              <Button title="upload" appearance="secondary" size="sm"
                onClick={() => setToggleAuditLog(!toggleAuditLog)}
                dataMetrics="cloudngfw-tokens-edit-auditLog"
                icon={<SettingsIcon size="sm" />}>
                {translate('generic.edit')}

              </Button>
            } isBackgroundFetching={tenantSettingsLoading} />
            <CardBody>
              <div className={styles.cardContent}>
                <div><Label label={translate(`resources.tokens.auditLog.auditLog`)} /></div>
                <div>{getAuditTrailText()}</div>
                <div><Label label={translate(`resources.tokens.auditLog.group`)} /></div>
                <div>{AuditTrail}</div>
              </div>
            </CardBody>
          </Card>
          <Card>
            <CardHeader addClassName={styles.carHeaderBorder} title={translate(`resources.tokens.panels.support`)} isBackgroundFetching={supportInfoLoading} />
            <CardBody>
              <div className={styles.cardContent}>
                <div><Label label={translate(`resources.tokens.fields.tenantId`)} /></div>
                <div className={styles.cardFlexContent}>
                  {cspSupportInfo?.ExternalId && <>
                    <div><CopyButton size='sm' addClassName='tw-p-0' text={cspSupportInfo?.ExternalId} /></div>
                    <div>{cspSupportInfo?.ExternalId}</div>
                  </>}
                </div>
                <div><Label label={translate(`resources.tokens.fields.serialNumber`)} /></div>
                <div className={styles.cardFlexContent}>
                  {cspSupportInfo?.SupportInformations?.[0]?.SerialNumber && <>
                    <div><CopyButton size='sm' addClassName='tw-p-0' text={cspSupportInfo?.SupportInformations?.[0]?.SerialNumber} /></div>
                    <div>{cspSupportInfo?.SupportInformations?.[0]?.SerialNumber}</div>
                  </>}
                </div>
                <div><Label label={translate(`resources.tokens.fields.supportAccount`)} /></div>
                <div>{cspSupportInfo?.SupportInformations?.[0]?.SupportAccountId}</div>
                <div><Label label={translate(`resources.tokens.fields.supportType`)} /></div>
                <div>{(cspSupportInfo?.SupportInformations?.[0]?.SupportType === SUPPORT_ENHANCED) ? SUPPORT_PREMIUM : SUPPORT_STANDARD}</div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div >
      <EditAuditLogSheet key={`${toggleAuditLog}`}
        isOpen={toggleAuditLog}
        setOpen={setToggleAuditLog}
        onSubmit={updateAuditLog}
        defaultValues={{ AuditTrail, EnableAuditTrail }}
      />
    </>
  );
};
export default Tenant;
